import React, { useState, useEffect, useRef, useCallback, ChangeEvent } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    InputBase,
    IconButton,
    Paper,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Avatar,
    Typography,
    CircularProgress,
    Link as MuiLink,
    ListItemButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import classes from "./SearchBar.module.scss";
import {
    setSearchQuery,
    setSearchType,
    fetchSearchSuggestions,
    clearSuggestions,
    resetFullSearch,
} from "../../../../store/slices/searchSlice";
import { RootState, AppDispatch } from "../../../../store/store"; // Assuming store.ts exports these types
import { UserSuggestion, PostSuggestion } from "../../../../store/slices/searchSlice"; // Import specific types

// Define the props interface
interface SearchBarProps {
    isAuthenticated: boolean;
    isTabletUp: boolean;
    currentPathname: string;
    searchValue: string;
    onSearchChange: (event: ChangeEvent<HTMLInputElement>) => void;
    onClearSearch: () => void;
}

// Array of generic messages for users without an about snippet
const GENERIC_USER_MESSAGES = [
    "Salam! 👋",
    "Looking forward to connecting!",
    "Just joined MAX Aspire!",
    "Excited to be part of this community!",
    "Ready to network and grow!",
    "Say hi if you see my profile!",
    "Exploring new opportunities.",
];

// Date formatting utility
const formatDateSuggestion = (dateString?: string): string => {
    if (!dateString) {
        return "Date N/A";
    }
    try {
        const date = new Date(dateString);
        // Check if date is valid
        if (isNaN(date.getTime())) {
            return "Invalid Date";
        }
        return date.toLocaleDateString(undefined, {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    } catch (e) {
        // Catch any other errors during date parsing, though unlikely if isNaN is checked
        return "Invalid Date";
    }
};

// Debounce utility function
function debounce<F extends (...args: any[]) => any>(func: F, waitFor: number) {
    let timeout: ReturnType<typeof setTimeout> | null = null;

    const debounced = (...args: Parameters<F>) => {
        if (timeout !== null) {
            clearTimeout(timeout);
            timeout = null;
        }
        timeout = setTimeout(() => func(...args), waitFor);
    };

    return debounced as (...args: Parameters<F>) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
    isAuthenticated, // We can decide later if SearchBar should use these props
    isTabletUp,
    currentPathname,
    searchValue: propSearchValue, // Renaming to avoid conflict with local state if needed, though not used yet
    onSearchChange: propOnSearchChange,
    onClearSearch: propOnClearSearch,
}) => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const [inputValue, setInputValue] = useState(propSearchValue || ""); // Initialize with prop or empty

    useEffect(() => {
        setInputValue(propSearchValue || ""); // Sync with prop changes
    }, [propSearchValue]);

    const [isDropdownOpen, setDropdownOpen] = useState(false);
    const anchorEl = useRef<HTMLDivElement>(null); // For Popper positioning

    const {
        searchQuery, // This is from Redux, propSearchValue is the value from Navbar
        searchType,
        suggestions,
        isLoadingSuggestions,
        suggestionsError,
    } = useSelector((state: RootState) => state.search);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newQuery = event.target.value;
        propOnSearchChange(event); // Call the prop function passed from Navbar

        if (newQuery.trim() === "") {
            dispatch(clearSuggestions());
            setDropdownOpen(false);
        } else {
            setDropdownOpen(true);
            debouncedFetchSuggestions(newQuery, searchType);
        }
    };

    const debouncedFetchSuggestions = useCallback(
        debounce((currentQuery: string, currentSearchType: "posts" | "users") => {
            if (currentQuery.trim() !== "") {
                console.log(
                    `[SearchBar] Dispatching fetchSearchSuggestions with query: "${currentQuery}", type: "${currentSearchType}"`,
                );
                dispatch(fetchSearchSuggestions({ query: currentQuery, type: currentSearchType }))
                    .unwrap()
                    .then((data) => {
                        console.log(
                            `[SearchBar] fetchSearchSuggestions successful for query: "${currentQuery}", type: "${currentSearchType}". Received:`,
                            data,
                        );
                    })
                    .catch((error) => {
                        console.error(
                            `[SearchBar] fetchSearchSuggestions failed for query: "${currentQuery}", type: "${currentSearchType}". Error:`,
                            error,
                        );
                    });
            } else {
                dispatch(clearSuggestions());
            }
        }, 300),
        [dispatch],
    );

    const handleTabChange = (event: React.SyntheticEvent, newValue: "posts" | "users") => {
        dispatch(setSearchType(newValue));
        if (inputValue.trim()) {
            console.log(
                `[SearchBar] Re-fetching suggestions due to tab change. Query: "${inputValue}", New Type: "${newValue}"`,
            );
            dispatch(fetchSearchSuggestions({ query: inputValue, type: newValue }))
                .unwrap()
                .then((data) => {
                    console.log(
                        `[SearchBar] fetchSearchSuggestions (tab change) successful for query: "${inputValue}", type: "${newValue}". Received:`,
                        data,
                    );
                })
                .catch((error) => {
                    console.error(
                        `[SearchBar] fetchSearchSuggestions (tab change) failed for query: "${inputValue}", type: "${newValue}". Error:`,
                        error,
                    );
                });
        }
    };

    const handleClearInput = () => {
        propOnClearSearch(); // Call the prop function from Navbar
        setDropdownOpen(false);
    };

    const handleFocus = () => {
        setDropdownOpen(true); // Always open on focus
    };

    const handleSuggestionClick = (
        suggestion: UserSuggestion | PostSuggestion,
        type: "users" | "posts",
    ) => {
        setDropdownOpen(false);
        if (type === "users") {
            navigate(`/profile/${(suggestion as UserSuggestion).userId}`);
        } else {
            navigate(`/newsfeed/${(suggestion as PostSuggestion).postId}`);
        }
        handleClearInput(); // Optionally clear search after selection
    };

    const handleSeeAllResults = () => {
        if (searchQuery.trim() !== "") {
            setDropdownOpen(false);
            dispatch(resetFullSearch()); // Reset pagination for new full search
            navigate(`/search-results?q=${encodeURIComponent(searchQuery)}&type=${searchType}`);
        }
    };

    const handleInputKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            handleSeeAllResults();
        }
    };

    // Handle clicks outside to close dropdown
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (anchorEl.current && !anchorEl.current.contains(event.target as Node)) {
                setDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const currentSuggestions = searchType === "users" ? suggestions.users : suggestions.posts;
    const hasSuggestions = currentSuggestions.length > 0;
    const showSeeAllButton = hasSuggestions && inputValue.trim() !== "";

    return (
        <Box className={classes.searchBarWrapper} ref={anchorEl}>
            {/* Main Search Input Container */}
            <Box className={classes.searchContainer}>
                <SearchIcon className={classes.searchIcon} />
                <InputBase
                    placeholder="Search for posts or users..."
                    value={inputValue}
                    onChange={handleInputChange}
                    onFocus={handleFocus}
                    onKeyDown={handleInputKeyDown}
                    fullWidth
                    classes={{
                        root: classes.searchInput,
                        input: classes.inputRoot,
                    }}
                    inputProps={{ "aria-label": "search" }}
                />
                {/* Clear Input Button */}
                {inputValue && (
                    <IconButton
                        size="small"
                        onClick={handleClearInput}
                        className={classes.clearButton}
                        aria-label="clear search"
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                )}
            </Box>

            {/* Dropdown Paper for Suggestions */}
            {isDropdownOpen && (
                <Paper className={classes.dropdownPaper} elevation={3}>
                    {/* Tabs for Selecting Search Type (Posts/Users) */}
                    <Tabs
                        value={searchType}
                        onChange={handleTabChange}
                        indicatorColor="primary"
                        textColor="primary"
                        variant="fullWidth"
                    >
                        <Tab
                            label="Posts"
                            value="posts"
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#f2f4f7",
                                },
                                "&.Mui-selected:hover": {
                                    // Optional: style selected tab's hover differently
                                    backgroundColor: "#f2f4f7", // Same for now, can adjust
                                },
                            }}
                        />
                        <Tab
                            label="Users"
                            value="users"
                            sx={{
                                "&:hover": {
                                    backgroundColor: "#f2f4f7",
                                },
                                "&.Mui-selected:hover": {
                                    backgroundColor: "#f2f4f7",
                                },
                            }}
                        />
                    </Tabs>

                    {/* Loading Indicator for Suggestions */}
                    {isLoadingSuggestions && (
                        <Box className={classes.loaderContainer}>
                            <CircularProgress size={24} />
                        </Box>
                    )}
                    {/* Error Message for Suggestions */}
                    {suggestionsError && (
                        <Typography color="error" className={classes.errorText}>
                            {suggestionsError}
                        </Typography>
                    )}
                    {/* "No Suggestions" Message */}
                    {!isLoadingSuggestions &&
                        !suggestionsError &&
                        currentSuggestions.length === 0 &&
                        inputValue.trim() !== "" && (
                            <Typography className={classes.noSuggestionsText}>
                                Nothing found for &quot;{inputValue}&quot; in {searchType}.
                            </Typography>
                        )}

                    {/* List of Suggestions */}
                    {!isLoadingSuggestions && !suggestionsError && hasSuggestions && (
                        <List dense disablePadding>
                            {/* Post Suggestions */}
                            {searchType === "posts" &&
                                suggestions.posts.map((post) => (
                                    <ListItemButton
                                        key={post.postId}
                                        onClick={() => handleSuggestionClick(post, "posts")}
                                        className={classes.suggestionItem}
                                    >
                                        <ListItemIcon sx={{ minWidth: "40px" }}>
                                            <Avatar
                                                src={post.authorProfileImageUrl || undefined}
                                                alt={post.authorFullName}
                                                sx={{ width: 32, height: 32 }}
                                            />
                                        </ListItemIcon>
                                        <ListItemText
                                            primaryTypographyProps={{
                                                className: classes.suggestionTextPrimary,
                                                variant: "body2",
                                            }}
                                            secondaryTypographyProps={{
                                                className: classes.suggestionTextSecondary,
                                                variant: "caption",
                                            }}
                                            primary={post.snippet}
                                            secondary={`${post.authorFullName || "Author N/A"} • ${formatDateSuggestion(post.postDate || post.createdAt)}`}
                                        />
                                    </ListItemButton>
                                ))}
                            {/* User Suggestions */}
                            {searchType === "users" &&
                                suggestions.users.map((user) => {
                                    const fullName =
                                        `${user.firstName || ""} ${user.lastName || ""}`.trim();
                                    return (
                                        <ListItemButton
                                            key={user.userId}
                                            onClick={() => handleSuggestionClick(user, "users")}
                                            className={classes.suggestionItem}
                                        >
                                            <ListItemIcon sx={{ minWidth: "40px" }}>
                                                <Avatar
                                                    src={user.photoUrl || undefined}
                                                    alt={fullName}
                                                    sx={{ width: 32, height: 32 }}
                                                />
                                            </ListItemIcon>
                                            <ListItemText
                                                primaryTypographyProps={{
                                                    className: classes.suggestionTextPrimary,
                                                    fontWeight: "bold",
                                                    variant: "body2",
                                                }}
                                                secondaryTypographyProps={{
                                                    className: classes.suggestionTextSecondary,
                                                    variant: "caption",
                                                }}
                                                primary={fullName}
                                                secondary={
                                                    user.aboutSnippet &&
                                                    user.aboutSnippet.trim() !== ""
                                                        ? user.aboutSnippet
                                                        : GENERIC_USER_MESSAGES[
                                                              Math.floor(
                                                                  Math.random() *
                                                                      GENERIC_USER_MESSAGES.length,
                                                              )
                                                          ]
                                                }
                                            />
                                        </ListItemButton>
                                    );
                                })}
                        </List>
                    )}

                    {/* "See All Results" Link/Button */}
                    {/* TODO: Uncomment this once we have a nice looking search results page */}
                    {/* {showSeeAllButton && (
                        <Box className={classes.seeAllContainer}>
                            <MuiLink
                                component="button"
                                variant="body2"
                                onClick={handleSeeAllResults}
                                className={classes.seeAllLink}
                            >
                                See all results for &quot;{inputValue}&quot;
                            </MuiLink>
                        </Box>
                    )} */}
                </Paper>
            )}
        </Box>
    );
};

export default SearchBar;
